import { inject, Injectable, signal } from '@angular/core';
import { CurrentUserService } from '../../../common/current-user/current-user.service';
import { CreateBriefService } from '../../briefs/service/create-brief/create-brief.service';
import { ChatGptService } from '../../../services/chat-gpt/chat-gpt.service';
import { GptCompletionMessage } from '../../../common/general-types/open-ai.dto';
import { IcpSearchService } from '../../create-brief-basic/steps/creator-profile/service/icp-search/icp-search.service';
import { BriefService } from '../../../services/brief/brief.service';
import { BRIEF_BUILDER_GOALS, BriefBuilderGoal, FOLLOWER_RANGES } from '../brief-builder.constants';
import {
  CommonalityItem,
  ContentSummaryAnalysisDto,
  RetroBriefDraftResult,
  RetroSuggestedBriefResult,
} from '../../../common/general-types/retro-v2.dto';
import { UgcProductResponseDto } from '../../../common/general-types/products.dto';
import {
  BRIEF_AI_JSON_DELIMITER,
  extractThoughtsFromStream,
  parseJsonAfterDelimiter,
} from './brief-ai-stream';
import { addChannel, createDefaultPieceEditor, piecesFromEditor } from '@/app/common/brief-pieces/piece-list';

type CreativeConcept = { id: number; name: string; description: string };

type AgeBracket = { id: number; label: string; minAge: number; maxAge: number };

export type DraftAudience = {
  genderIds?: number[];
  ageIds?: number[];
  is18plus?: boolean;
  interestIds?: number[];
  followerRanges?: { min: number; max: number }[];
  countryCodes?: string[];
};

export type DraftResult = Omit<RetroBriefDraftResult, 'synopsis'>;

export interface RetroDraftParams {
  sourceBriefId: string;
  sourceBriefTitle: string;
  sourceBriefGoalName: string;
  sourceBriefGoalDescription: string;
  product?: UgcProductResponseDto;
  goal?: { id: number; name: string; description: string; color: string };
  approvedCommonalities: CommonalityItem[];
  contentSummary?: ContentSummaryAnalysisDto;
}

interface BriefMessageContext {
  brandName: string;
  brandAbout: string;
  briefTitle: string;
  productName: string;
  productDescription: string;
  goal?: { id: number; name: string; description: string; color: string };
}

const VALID_GENDER_IDS = new Set([0, 1, 2, 3]);
const VALID_COUNTRY_CODES = new Set(['GB', 'US', 'IE']);
const KID_AGE_BRACKET_ID = 1;

/**
 * Wraps the proven Noodle/ChatGPT brief-draft generation (ported from AiDocumentationV1)
 * so both the AI-first and wizard flows can trigger it without the WYSIWYG UI.
 */
@Injectable({ providedIn: 'root' })
export class BriefAiService {
  private readonly currentUserService = inject(CurrentUserService);
  private readonly createBriefService = inject(CreateBriefService);
  private readonly chatGptService = inject(ChatGptService);
  private readonly icpSearchService = inject(IcpSearchService);
  private readonly briefService = inject(BriefService);

  readonly isDrafting = signal(false);
  readonly isCompilingBrief = signal(false);
  readonly streamingThoughts = signal('');
  readonly draftError = signal<string | undefined>(undefined);

  /**
   * Streams a full creative draft and populates the brief signals
   * (title, goal, audience, steps/description, dos, donts, concepts, caption, hashtags).
   * @param onThoughts optional callback receiving the streamed reasoning prose.
   * @returns true on success.
   */
  async draftBrief(anglePrompt: string, onThoughts?: (text: string) => void): Promise<boolean> {
    if (this.isDrafting()) {
      return false;
    }

    this.isDrafting.set(true);
    this.isCompilingBrief.set(false);
    this.streamingThoughts.set('');
    this.draftError.set(undefined);

    let full = '';
    try {
      for await (const delta of this.chatGptService.streamCompletionMessage(this.buildMessages(anglePrompt))) {
        full += delta;
        const thoughts = extractThoughtsFromStream(full);
        this.streamingThoughts.set(thoughts);
        if (full.includes(BRIEF_AI_JSON_DELIMITER)) {
          this.isCompilingBrief.set(true);
        }
        onThoughts?.(thoughts);
      }

      const parsed = this.parseDraftFromResponse(full);
      if (!parsed) {
        this.draftError.set('The AI response could not be read. Please try again.');
        return false;
      }

      const applied = this.applyDraftResult(parsed);
      if (applied) {
        this.seedPieceList();
      }
      return applied;
    } catch (err) {
      console.error('BriefAiService.draftBrief() failed', err);
      this.draftError.set('Something went wrong while generating your brief. Please try again.');
      return false;
    } finally {
      this.isDrafting.set(false);
      this.isCompilingBrief.set(false);
    }
  }

  /**
   * Generates a follow-up brief from retro performance insights without mutating CreateBriefService.
   * Returns the parsed draft and synopsis for display on the retro page.
   */
  async draftBriefFromRetro(params: RetroDraftParams): Promise<RetroSuggestedBriefResult | null> {
    if (this.isDrafting()) {
      return null;
    }

    this.isDrafting.set(true);
    this.isCompilingBrief.set(false);
    this.streamingThoughts.set('');
    this.draftError.set(undefined);

    let full = '';
    try {
      for await (const delta of this.chatGptService.streamCompletionMessage(this.buildRetroMessages(params))) {
        full += delta;
        const thoughts = extractThoughtsFromStream(full);
        this.streamingThoughts.set(thoughts);
        if (full.includes(BRIEF_AI_JSON_DELIMITER)) {
          this.isCompilingBrief.set(true);
        }
      }

      const parsed = this.parseDraftFromResponse(full, true);
      if (!parsed?.synopsis?.trim()) {
        this.draftError.set('The AI response could not be read. Please try again.');
        return null;
      }

      const { synopsis, ...draft } = parsed;
      return {
        synopsis: synopsis!.trim(),
        draft,
        sourceBriefId: params.sourceBriefId,
      };
    } catch (err) {
      console.error('BriefAiService.draftBriefFromRetro() failed', err);
      this.draftError.set('Something went wrong while generating your brief. Please try again.');
      return null;
    } finally {
      this.isDrafting.set(false);
      this.isCompilingBrief.set(false);
    }
  }

  /** Applies a parsed GPT draft into CreateBriefService signals. */
  applyDraftResult(parsed: DraftResult): boolean {
    const service = this.createBriefService;
    this.applyBasics(parsed);
    this.applyAudience(parsed.audience);
    service.stepsHtml.set(parsed.briefText ?? '');
    service.description.set(parsed.briefText ?? '');
    service.doHtml.set(parsed.dos ?? '');
    service.dontHtml.set(parsed.donts ?? '');
    service.creativeConcepts.set(this.normaliseConcepts(parsed.creativeConcepts));
    service.suggestedCaption.set(parsed.suggestedCaption ?? '');
    service.suggestedHastags.set(this.ensureAdTag(parsed.suggestedHashtags ?? ''));
    return true;
  }

  /**
   * A regular brief cannot launch without a piece list. The draft does not choose
   * placements, so the review starts with one TikTok post the brand can change.
   */
  private seedPieceList(): void {
    const service = this.createBriefService;
    if (service.pieces()?.length || service.pieceEditor()) {
      return;
    }
    const editor = addChannel(
      createDefaultPieceEditor({
        caption: service.suggestedCaption(),
        hashtags: service.suggestedHastags(),
        callToActionLink: service.callToActionLink(),
      }),
      0,
      'tiktok_post',
    );
    service.pieceEditor.set(editor);
    const pieces = piecesFromEditor(editor);
    service.pieces.set(pieces);
    service.videosPerCreator.set(Math.max(1, pieces?.length ?? 0));
  }

  /** Suggests a creator persona via the ICP search endpoint and populates creatorProfileModel. */
  async suggestPersona(): Promise<boolean> {
    const brand = this.currentUserService.focussedBrand;
    const product = this.createBriefService.selectedProduct();
    const goal = this.createBriefService.selectedBriefGoal();
    if (!product || !goal) {
      return false;
    }
    try {
      const result = await this.icpSearchService.openAiCreatorProfileSearch(
        brand?.companyName ?? '',
        this.createBriefService.basicInfoModel().briefTitle,
        product,
        goal,
      );
      const persona = Array.isArray(result) ? result[0] : result;
      if (persona && persona.creator_persona) {
        this.createBriefService.creatorProfileModel.set({
          creator_persona: persona.creator_persona ?? '',
          creator_audience: persona.creator_audience ?? '',
          creator_content_style: persona.creator_content_style ?? '',
          creator_engagement_metrics: persona.creator_engagement_metrics ?? '',
          creator_specific_characteristics: persona.creator_specific_characteristics ?? '',
          search_string: persona.search_string ?? '',
        });
        return true;
      }
      return false;
    } catch (err) {
      console.error('BriefAiService.suggestPersona() failed', err);
      return false;
    }
  }

  private buildMessageContext(overrides?: Partial<BriefMessageContext>): BriefMessageContext {
    const brand = this.currentUserService.focussedBrand;
    const product = this.createBriefService.selectedProduct();
    const goal = this.createBriefService.selectedBriefGoal();
    const briefTitle = this.createBriefService.basicInfoModel()?.briefTitle;

    return {
      brandName: overrides?.brandName ?? brand?.companyName ?? '',
      brandAbout: overrides?.brandAbout ?? brand?.about ?? '',
      briefTitle: overrides?.briefTitle ?? briefTitle ?? '',
      productName: overrides?.productName ?? product?.productName ?? '',
      productDescription: overrides?.productDescription ?? product?.productDescription ?? '',
      goal: overrides?.goal ?? goal,
    };
  }

  private buildMessages(
    anglePrompt: string,
    contextOverrides?: Partial<BriefMessageContext>,
    options?: { includeSynopsis?: boolean },
  ): GptCompletionMessage[] {
    const context = this.buildMessageContext(contextOverrides);
    const goalCatalog = this.formatGoalCatalog();
    const genderCatalog = this.briefService.creatorGenders.map((g) => `${g.id}=${g.name}`).join(', ');
    const ageCatalog = this.briefService.creatorAges.map((a) => `${a.id}=${a.label}`).join(', ');
    const interestCatalog = this.briefService.creatorInterests.map((i) => `${i.id}=${i.name}`).join(', ');
    const countryCatalog = this.briefService.countryCodes.map((c) => c.countryCode).join(', ');
    const followerCatalog = FOLLOWER_RANGES.map((r) => `{min:${r.min},max:${r.max}} (${r.label})`).join(', ');

    const goalContext = context.goal
      ? `This is the goal of the campaign: ${context.goal.name}: ${context.goal.description}`
      : `No campaign goal has been chosen yet. Pick the best matching goal from this list and set campaignGoalName to its exact name: ${goalCatalog}`;

    const systemMessage: GptCompletionMessage = {
      role: 'system',
      content: `You are a social media expert helping a brand come up with a brief to instruct content creators.
        First, narrate your reasoning out loud in plain, friendly prose so the user can follow your thinking — do NOT use JSON during this part.
        When you have finished reasoning, output the exact delimiter line ${BRIEF_AI_JSON_DELIMITER} on its own line, then output ONLY a single valid, RFC8259 compliant JSON object and nothing after it.
        Do not wrap the JSON in markdown code fences. Never mention the term "JSON" at any point.`,
    };

    const userMessage: GptCompletionMessage = {
      role: 'user',
      content: `This is my brand name: ${context.brandName}
      This is my brand description: ${context.brandAbout}
      ${context.briefTitle ? 'This is the current brief title: ' + context.briefTitle : ''}
      This is the product name they are advertising: ${context.productName}
      This is the product description: ${context.productDescription}
      ${goalContext}

      Valid option catalogs (use these exact IDs/codes in the audience object):
      Campaign goals (use campaignGoalName with the exact name string): ${goalCatalog}
      Genders (genderIds): ${genderCatalog}
      Age brackets (ageIds): ${ageCatalog}
      Interests (interestIds): ${interestCatalog}
      Country codes (countryCodes): ${countryCatalog}
      Follower tiers (followerRanges as {min,max} objects): ${followerCatalog}

      Here is the format of the JSON object you must produce after the ${BRIEF_AI_JSON_DELIMITER} delimiter:
      {
        "briefTitle": string,
        "campaignGoalName": string,
        "audience": {
          "genderIds": number[],
          "ageIds": number[],
          "is18plus": boolean,
          "interestIds": number[],
          "followerRanges": { "min": number, "max": number }[],
          "countryCodes": string[]
        },
        "briefText": string,
        "dos": string,
        "donts": string,
        "creativeConcepts": { "id": number, "name": string, "description": string }[],
        "suggestedCaption": string,
        "suggestedHashtags": string${options?.includeSynopsis ? ',\n        "synopsis": string' : ''}
      }

      briefTitle is a punchy Title Case name tied to this product and campaign (max ~80 characters).
      campaignGoalName must exactly match one name from the campaign goals catalog above.
      audience should reflect a sensible target creator demographic for this product and campaign: pick 1–3 interestIds, 1–2 ageIds, 1 followerRanges tier, 1+ countryCodes; use genderIds [0] for any gender or specific IDs; set is18plus to true when minors are inappropriate for this product/campaign.
      briefText is an HTML string: a clear brief with headings "Overview", "What we're looking for in your content", and "Some client USP's for you to include in your content".
      dos is an HTML string of things a creator should do. donts is an HTML string of things to avoid (include: don't post before approval if this is a social brief; no explicit content; no competing brands; no third-party trademarks) — rewrite these specifically for this brand/product.
      Include 2 to 4 distinct creativeConcepts (never more than 4), ids starting at 0.
      suggestedCaption is plain-text on-brand caption. suggestedHashtags is plain-text space-separated hashtags and MUST include "#ad".
      ${options?.includeSynopsis ? 'synopsis is a plain-text overview of the suggested follow-up brief in 2–3 sentences for the brand team.\n      ' : ''}Use Tailwind classes in the HTML. Write SPECIFIC instructions tied to this exact brand, product and campaign — not generic ones.
      Do NOT mention JSON in your reasoning prose.

      The creative direction the brand has chosen for this brief: ${anglePrompt}`,
    };

    return [systemMessage, userMessage];
  }

  private buildRetroMessages(params: RetroDraftParams): GptCompletionMessage[] {
    const brand = this.currentUserService.focussedBrand;
    const contextOverrides: Partial<BriefMessageContext> = {
      brandName: brand?.companyName ?? '',
      brandAbout: brand?.about ?? '',
      briefTitle: params.sourceBriefTitle,
      productName: params.product?.productName ?? '',
      productDescription: params.product?.productDescription ?? '',
      goal: params.goal,
    };

    const commonalitiesBlock = params.approvedCommonalities.length
      ? params.approvedCommonalities
          .map(
            (item) =>
              `- [${item.type}] "${item.statement}" (${item.count}/${item.totalCompared} top videos). Example: ${item.sampleExplanation}`,
          )
          .join('\n')
      : 'No shared review criteria were identified.';

    const summary = params.contentSummary;
    const contentThemesBlock = summary
      ? `Actions: ${summary.actionsSummary}
Overall tone: ${summary.overallToneSummary}
Content type: ${summary.contentTypeSummary}
Keywords: ${summary.keywordsByIndustrySummary}
Products shown: ${summary.productsShownSummary}`
      : 'No shared content themes were identified.';

    const retroAngle = `Build on what worked in "${params.sourceBriefTitle}" (${params.sourceBriefGoalName}: ${params.sourceBriefGoalDescription}).

TOP-PERFORMING VIDEO INSIGHTS — review criteria consistently passed ("Done well"):
${commonalitiesBlock}

CONTENT THEMES IN COMMON across top performers:
${contentThemesBlock}

Create a follow-up brief that instructs new creators to replicate these winning patterns. Encode every "Done well" criterion into dos and briefText. Creative concepts must reflect the shared content themes. Do not repeat the old brief verbatim — evolve it for the next round of creators.`;

    const [systemMessage, userMessage] = this.buildMessages(retroAngle, contextOverrides, { includeSynopsis: true });

    return [systemMessage, userMessage];
  }

  private formatGoalCatalog(): string {
    return BRIEF_BUILDER_GOALS.map((g) => g.name).join(', ');
  }

  private parseDraftFromResponse(full: string, expectSynopsis = false): RetroBriefDraftResult | null {
    const parsed = parseJsonAfterDelimiter<RetroBriefDraftResult>(full);
    if (!parsed) {
      return null;
    }
    if (expectSynopsis && !parsed.synopsis?.trim()) {
      return null;
    }
    return parsed;
  }

  private applyBasics(parsed: DraftResult): void {
    const title = parsed.briefTitle?.trim();
    if (title) {
      this.createBriefService.basicInfoModel.set({ briefTitle: title });
    }

    const goal = this.findBriefGoalByName(parsed.campaignGoalName);
    if (goal) {
      this.createBriefService.selectedBriefGoal.set({
        id: goal.id,
        name: goal.name,
        description: goal.description,
        color: goal.color,
      });
    }
  }

  private applyAudience(audience: DraftAudience | undefined): void {
    if (!audience) {
      return;
    }

    const service = this.createBriefService;

    if (Array.isArray(audience.genderIds)) {
      const genderIds = audience.genderIds.filter((id) => VALID_GENDER_IDS.has(id));
      service.selectedGenders.set(genderIds.includes(0) || genderIds.length === 0 ? [] : genderIds);
    }

    if (Array.isArray(audience.ageIds)) {
      const validAgeIds = new Set(this.briefService.creatorAges.map((a) => a.id));
      let ages: AgeBracket[] = audience.ageIds
        .filter((id) => validAgeIds.has(id))
        .map((id) => this.briefService.creatorAges.find((a) => a.id === id))
        .filter((a): a is AgeBracket => a !== undefined);

      const is18plus = audience.is18plus === true;
      service.is18plus.set(is18plus);
      if (is18plus) {
        ages = ages.filter((a) => a.id !== KID_AGE_BRACKET_ID);
      }
      service.selectedAge.set(ages);
    } else if (audience.is18plus === true) {
      service.is18plus.set(true);
      service.selectedAge.update((ages) => ages.filter((a) => a.id !== KID_AGE_BRACKET_ID));
    } else if (audience.is18plus === false) {
      service.is18plus.set(false);
    }

    if (Array.isArray(audience.interestIds)) {
      const validInterestIds = new Set(this.briefService.creatorInterests.map((i) => Number(i.id)));
      service.selectedInterests.set(
        audience.interestIds.filter((id) => validInterestIds.has(Number(id))).map(Number),
      );
    }

    if (Array.isArray(audience.followerRanges)) {
      const validRanges = FOLLOWER_RANGES.map((r) => ({ min: r.min, max: r.max }));
      service.selectedFollowerRanges.set(
        audience.followerRanges.filter((range) =>
          validRanges.some((valid) => valid.min === range.min && valid.max === range.max),
        ),
      );
    }

    if (Array.isArray(audience.countryCodes)) {
      service.selectedCountryCodes.set(
        audience.countryCodes.filter((code) => VALID_COUNTRY_CODES.has(code)),
      );
    }
  }

  private findBriefGoalByName(name: string | undefined): BriefBuilderGoal | undefined {
    if (!name?.trim()) {
      return undefined;
    }
    return BRIEF_BUILDER_GOALS.find((goal) => goal.name === name.trim());
  }

  private ensureAdTag(hashtags: string): string {
    const trimmed = hashtags.trim();
    if (/(^|\s)#ad(\s|$)/i.test(trimmed)) {
      return trimmed;
    }
    return trimmed.length > 0 ? `#ad ${trimmed}` : '#ad';
  }

  private normaliseConcepts(concepts: CreativeConcept[] | undefined): CreativeConcept[] {
    if (!Array.isArray(concepts)) {
      return [];
    }
    return concepts.map((concept, index) => ({
      id: typeof concept.id === 'number' ? concept.id : index,
      name: concept.name ?? '',
      description: concept.description ?? '',
    }));
  }
}
